// Import animal data from animalData.js
import { allAnimals as importedAnimals } from '../../data/animalData';

// Export the imported animals
export const allAnimals = importedAnimals;

// --- Utility Functions ---

// Rarity order mapping
export const RARITY_ORDER = {
  'Common': 0,
  'Uncommon': 1,
  'Rare': 2,
  'Epic': 3,
  'Legendary': 4
};

// Product info mapping
const productInfo = {
  'Eggs': { 
    id: 'eggs', 
    image: 'egg',
    tabId: 'eggs',
    foodPath: '/food/birds'
  },
  'Milk': { 
    id: 'milk', 
    image: 'milk',
    tabId: 'milk',
    foodPath: '/food/cattle'
  },
  'Wool': { 
    id: 'wool', 
    image: 'fuzzies',
    tabId: 'wool',
    foodPath: '/food/fuzzies'
  },
  'Truffle': {
    id: 'truffle',
    image: 'truffle',
    tabId: 'truffle',
    foodPath: '/food/piggies'
  }
};

// Food type mapping
const foodTypeMap = {
  'birds': 'bird_feed',
  'cattle': 'cattle_feed',
  'fuzzies': 'fuzzy_feed',
  'piggies': 'pig_feed'
};

/**
 * Get filtered animals by type
 */
export function getFilteredAnimals(animalType) {
  if (!animalType) return [];
  return animalType.variants.flatMap(variant => 
    variant.rarity.map(rarity => ({
      id: `${variant.id}-${rarity.type.toLowerCase()}`,
      name: rarity.name || variant.name,
      variantName: variant.name,
      rarity: [rarity],
      productionTime: animalType.productionTime,
      biopoints: rarity.biopoints,
      image: rarity.image,
      cardImage: rarity.cardImage,
      product: animalType.product,
      feedType: animalType.feedType,
      type: animalType.name,
      variantId: variant.id,
      rarityType: rarity.type,
      upgradeRequirements: rarity.upgradeRequirements || []
    }))
  );
}

/**
 * Get rarity multiplier
 */
export function getRarityMultiplier(rarity) {
  const multipliers = {
    'Common': 1,
    'Uncommon': 1.5,
    'Rare': 2,
    'Epic': 2.5,
    'Legendary': 3
  };
  return multipliers[rarity] || 1;
}

/**
 * Get product info for an animal
 */
export function getProductInfoForAnimal(animal) {
  if (!animal) return null;
  
  const product = animal.product || '';
  
  if (productInfo[product]) {
    return productInfo[product];
  }
  
  // Default product info
  return {
    id: product.toLowerCase(),
    image: product.toLowerCase(),
    tabId: product.toLowerCase(),
    foodPath: `/food/${animal.type.toLowerCase()}`
  };
}

/**
 * Get food info for an animal
 */
export function getFoodInfoForAnimal(animal) {
  if (!animal) return null;
  
  const animalType = animal.type ? animal.type.toLowerCase() : '';
  const feedType = foodTypeMap[animalType] || 'feed';
  
  return {
    type: feedType,
    name: feedType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  };
}

/**
 * Format production time in hours and minutes
 */
export function formatProductionTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return { hours, minutes };
}

// Export animal types
export const animalTypes = Object.entries(allAnimals).map(([key, value]) => ({
  id: key,
  name: value.name,
  product: value.product,
  feedType: value.feedType,
  productionTime: value.productionTime,
  image: key === 'fuzzies' || key === 'piggies' 
    ? `/images/beds/${key}/common/preview.png` 
    : `/images/animals/${key}/common/preview.png`,
  rarities: value.variants[0]?.rarity.map(r => r.type) || [],
  get variants() {
    return value.variants.flatMap(variant => 
      variant.rarity.map(rarity => ({
        ...variant,
        ...rarity,
        id: `${variant.id}-${rarity.type.toLowerCase()}`,
        name: rarity.name || variant.name,
        variantName: variant.name,
        rarityType: rarity.type,
        rarity: [rarity],
        image: rarity.image,
        cardImage: rarity.cardImage,
        biopoints: rarity.biopoints,
        productionTime: value.productionTime,
        product: value.product,
        feedType: value.feedType,
        type: value.name,
        variantId: variant.id,
        upgradeRequirements: rarity.upgradeRequirements || []
      }))
    );
  }
}));

// Export animal products
const animalToProductMap = {
  'chicken': 'Eggs',
  'turkey': 'Eggs',
  'duck': 'Eggs',
  'cow': 'Milk',
  'buffalo': 'Milk',
  'sheep': 'Wool',
  'alpaca': 'Wool',
  'pig': 'Truffle'
};

/**
 * Get product type for an animal
 */
export function getProductTypeForAnimal(animal) {
  if (!animal || !animal.id) return 'Eggs';
  const baseId = animal.id.split('-')[0];
  return animalToProductMap[baseId] || 'Eggs';
}
/**
 * Format production time in hours and minutes
 */

export const animalProducts = [
  {
    id: 'eggs',
    name: 'Eggs',
    description: 'Eggs are produced by birds. The quality of eggs depends on the bird\'s rarity and the quality of feed used.',
    image: '/images/vegetables/egg/preview.png',
    variants: [
      { rarity: 'Common', biopoints: 18.3, productionTime: 1800 },
      { rarity: 'Uncommon', biopoints: 63, productionTime: 1800 },
      { rarity: 'Rare', biopoints: 91, productionTime: 1800 },
      { rarity: 'Epic', biopoints: 123, productionTime: 1800 },
      { rarity: 'Legendary', biopoints: 224, productionTime: 1800 }
    ],
    sourceAnimals: ['Birds']
  },
  {
    id: 'milk',
    name: 'Milk',
    description: 'Milk is produced by cattle. The quality of milk depends on the cattle\'s rarity and the quality of feed used.',
    image: '/images/vegetables/milk/preview.png',
    variants: [
      { rarity: 'Common', biopoints: 272, productionTime: 6600 },
      { rarity: 'Uncommon', biopoints: 466, productionTime: 6600 },
      { rarity: 'Rare', biopoints: 661, productionTime: 6600 },
      { rarity: 'Epic', biopoints: 855, productionTime: 6600 },
      { rarity: 'Legendary', biopoints: 1516, productionTime: 6600 }
    ],
    sourceAnimals: ['Cattle']
  },
  {
    id: 'wool',
    name: 'Wool',
    description: 'Wool is produced by fuzzies. The quality of wool depends on the fuzzy\'s rarity and the quality of feed used.',
    image: '/images/vegetables/fuzzies/preview.png',
    variants: [
      { rarity: 'Common', biopoints: 469, productionTime: 13800 },
      { rarity: 'Uncommon', biopoints: 863, productionTime: 13800 },
      { rarity: 'Rare', biopoints: 1334, productionTime: 13800 },
      { rarity: 'Epic', biopoints: 2194, productionTime: 13800 },
      { rarity: 'Legendary', biopoints: 3526, productionTime: 13800 }
    ],
    sourceAnimals: ['Fuzzies']
  }
]; 